import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import "./Confirm.scss";

/**
 * Confirm — Popup de confirmación estilo iOS liquid crystal
 *
 * ── Exports ───────────────────────────────────────────────────────────────────
 *   default  →  Confirm           Componente base (uso controlado)
 *   named    →  { useConfirm }    Hook imperativo basado en Promise
 *
 * ── IMPORTANTE: forma correcta de importar ────────────────────────────────────
 *
 *   // Hook (recomendado)
 *   import { useConfirm } from "@/Components/Atoms/Confirm/Confirm";
 *
 *   // Componente base solamente
 *   import Confirm from "@/Components/Atoms/Confirm/Confirm";
 *
 *   // Ambos a la vez
 *   import Confirm, { useConfirm } from "@/Components/Atoms/Confirm/Confirm";
 *
 * ── Props (componente base) ───────────────────────────────────────────────────
 *   open          bool     Controla visibilidad
 *   icon          string   Ícono Material Icons (opcional)
 *   title         string   Título del popup
 *   message       string   Mensaje descriptivo
 *   confirmText   string   Texto botón aceptar  (default: "Aceptar")
 *   cancelText    string   Texto botón cancelar (default: "Cancelar")
 *   onConfirm     fn       Callback al aceptar  → Promise resuelve true
 *   onClose       fn       Callback al cancelar / cerrar → Promise resuelve false
 *   requireWord   string   Palabra exacta que el usuario debe escribir para
 *                          habilitar el botón de confirmación (case-sensitive)
**/
// ── Componente base ────────────────────────────────────────────────────────────
const Confirm = ({
    open,
    icon,
    title,
    message,
    confirmText = "Aceptar",
    cancelText = "Cancelar",
    onConfirm,
    onClose,
    requireWord,
}) => {
    const [typed, setTyped] = useState("");

    // Resetear input al abrir / cerrar
    useEffect(() => {
        if (!open) setTyped("");
    }, [open]);

    // Bloquear scroll del body mientras está abierto
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    if (!open) return null;

    const wordMatch = !requireWord || typed.trim() === requireWord;

    return createPortal(
        <div className="confirm-overlay" onMouseDown={onClose}>
            <div
                className="confirm-box animate__animated animate__zoomIn"
                onMouseDown={e => e.stopPropagation()}
            >
                {icon && (
                    <div className="confirm-icon-wrap">
                        <span className="icon confirm-icon" data-icon={icon} />
                    </div>
                )}
                <div className="confirm-body">
                    {title && <span className="confirm-title">{title}</span>}
                    {message && <p className="confirm-message">{message}</p>}

                    {requireWord && (
                        <div className="confirm-word-wrap">
                            <span className="confirm-word-hint">
                                Escribe <strong>{requireWord}</strong> para continuar
                            </span>
                            <input
                                className={`confirm-word-input${!wordMatch && typed ? " confirm-word-input--error" : ""}`}
                                type="text"
                                autoFocus
                                autoComplete="off"
                                spellCheck="false"
                                value={typed}
                                onChange={e => setTyped(e.target.value)}
                                onKeyDown={e => e.key === "Enter" && wordMatch && onConfirm?.()}
                                placeholder={requireWord}
                            />
                        </div>
                    )}
                </div>

                <div className="confirm-actions">
                    {!onConfirm ? (
                        // Modo alerta: un solo botón de cierre
                        <button className="confirm-btn confirm-accept" type="button" onClick={onClose}>
                            {cancelText}
                        </button>
                    ) : (
                        // Modo confirmación: cancelar + aceptar
                        <>
                            <button className="confirm-btn confirm-cancel" type="button" onClick={onClose}>
                                {cancelText}
                            </button>
                            <button
                                className="confirm-btn confirm-accept"
                                type="button"
                                onClick={onConfirm}
                                disabled={!wordMatch}
                            >
                                {confirmText}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

// ── Hook imperativo basado en Promise ──────────────────────────────────────────
/**
 * useConfirm — hook que expone `confirm(options)` como una Promise<boolean>.
 *
 * La ejecución se pausa hasta que el usuario acepta (true) o cancela (false).
 *
 * ⚠️  OBLIGATORIO: montar {ConfirmDialog} en el JSX del componente.
 *     Sin este paso el portal nunca se renderiza y el diálogo no aparece,
 *     aunque confirm() se llame correctamente.
 *
 * Ejemplo mínimo:
 *
 *   const { confirm, ConfirmDialog } = useConfirm();
 *
 *   const handleAction = async () => {
 *     const ok = await confirm({ title: "¿Continuar?" });
 *     if (!ok) return;
 *     // ...
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleAction}>Acción</Button>
 *       {ConfirmDialog}   ← sin esto el modal no aparece
 *     </>
 *   );
 */
export const useConfirm = () => {
    const resolveRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState({});

    const confirm = useCallback((opts = {}) => {
        return new Promise((resolve) => {
            resolveRef.current = resolve;
            setOptions(opts);
            setOpen(true);
        });
    }, []);

    const handleConfirm = useCallback(() => {
        resolveRef.current?.(true);
        resolveRef.current = null;
        setOpen(false);
    }, []);

    const handleClose = useCallback(() => {
        resolveRef.current?.(false);
        resolveRef.current = null;
        setOpen(false);
    }, []);

    const ConfirmDialog = (
        <Confirm
            open={open}
            onConfirm={handleConfirm}
            onClose={handleClose}
            {...options}
        />
    );

    return { confirm, ConfirmDialog };
};

export default Confirm;
